/**
 * A mixin to be used in the parent component that contains an instance
 * of the ContentSelectionOverlay component.
 *
 * Handles store mutations and handling the result of cancellation or
 * confirmation of the overlay.
 *
 * Depends on:
 *   - this.contents
 *   - this.refetchContents()
 *   - this.$refs.contentselectionoverlay
 *   - @/mixins/contentSelection.js
 */

import {CONTENT_SELECTION_MODES} from '@/mixins/contentSelection.js';
import contentSelection from '@/mixins/contentSelection.js';

export default {
  mixins: [contentSelection],
  data() {
    return {
      busyProcessingSelectedContents: false,
    };
  },
  computed: {
    showContentSelectionOverlay() {
      return this.atLeastOneContentSelected;
    },
  },
  methods: {
    closeOverlayAndClearSelection() {
      this.$refs.contentselectionoverlay.deselectAllContents();
      this.$store.commit('RESET_SELECTED_CONTENTS_FOR_BATCH_ACTIVATION');
    },
    onContentSelectionOverlayAborted() {
      this.closeOverlayAndClearSelection();
    },
    onContentSelectionOverlayConfirmed(mode, selectedContents) {
      // Activate
      if (mode === CONTENT_SELECTION_MODES.activate) {
        this.$store.commit(
          'SET_SELECTED_CONTENTS_FOR_BATCH_ACTIVATION',
          selectedContents
        );
        this.$store.commit('UNSET_PRE_ROUTE_QUESTION');

        // Navigate last, so garbage collection does not interfere with actions above.
        this.$router.push('/content/selected/add');
      }
      // Deactivate
      else if (mode === CONTENT_SELECTION_MODES.deactivate) {
        this.deactivateSelectedContents(selectedContents);
      }
      // Delete
      else if (mode === CONTENT_SELECTION_MODES.delete) {
        const contentSelectionOverlayWidget =
          this.$refs.contentselectionoverlay;
        this.askDeleteSelectedContents(
          selectedContents,
          contentSelectionOverlayWidget
        );
      }
      // Bundle
      else if (mode === CONTENT_SELECTION_MODES.bundle) {
        this.bundleSelectedContents(selectedContents);
      }
      // Refresh Date
      else if (mode === CONTENT_SELECTION_MODES.refreshDate) {
        this.refreshDateSelectedContents(selectedContents);
      }
    },
    deactivateSelectedContents(selectedContents) {
      this.busyProcessingSelectedContents = true;
      return this.$api
        .deactivateSelectedContents(selectedContents)
        .then((response) => {
          const hasWarnings = response.data.warnings.length > 0;
          const alertType = hasWarnings ? 'alert-warning' : 'alert-success';
          const message = hasWarnings
            ? 'deactivateMultipleContentsTogetherSuccessPartial'
            : 'deactivateMultipleContentsTogetherSuccess';

          this.busyProcessingSelectedContents = false;
          this.$bus.$emit(alertType, {
            message: message,
            translate: true,
            timeout: 5000,
          });

          this.closeOverlayAndClearSelection();
          this.refetchContents();
        })
        .catch((error) => {
          console.error(error);
          this.$bus.$emit('alert-error', {
            message: 'genericFailedInfo',
            translate: true,
          });
        })
        .finally(() => {
          this.busyProcessingSelectedContents = false;
        });
    },
    askDeleteSelectedContents(
      selectedContents,
      contentSelectionOverlayWidget = null
    ) {
      if (contentSelectionOverlayWidget) {
        contentSelectionOverlayWidget.disabled = true;
      }

      const vm = this;
      const hint = this.$t('contentSelectionDeleteConfirmDialogBody');
      this.$bus.$emit('show-dynamic-dialog', {
        allowMobileFullscreen: false,
        confirmIcon: null,
        confirmLabel: this.$t('contentSelectionDeleteConfirmDialogButton'),
        content: `
          <div class="subheading">
            ${hint}
          </div>
        `,
        maxDialogWidth: 400,
        persistent: true,
        title: this.$t('contentSelectionModeDelete'),
        titleIcon: 'delete',
        onConfirm() {
          if (contentSelectionOverlayWidget) {
            contentSelectionOverlayWidget.disabled = false;
          }

          const dialog = this;
          dialog.loading = true;
          vm.deleteSelectedContents(selectedContents).then(() => {
            dialog.loading = false;
            vm.$bus.$emit('hide-dynamic-dialog');
            vm.closeOverlayAndClearSelection();
          });
        },
        onAbort() {
          if (contentSelectionOverlayWidget) {
            contentSelectionOverlayWidget.disabled = false;
            vm.$bus.$emit('hide-dynamic-dialog');
          }
        },
      });
    },
    deleteSelectedContents(selectedContents) {
      this.busyProcessingSelectedContents = true;
      return this.$api
        .deleteSelectedContents(selectedContents)
        .then((response) => {
          const hasWarnings = response.data.warnings.length > 0;

          // Allow showing feedback e.g. a report dialog when things failed.
          if (hasWarnings) {
            this.$emit('contentSelectionOverlayDeletionReport', {
              selectedContents: selectedContents,
              warnings: response.data.warnings,
            });
          }

          const alertType = hasWarnings ? 'alert-warning' : 'alert-success';
          const message = hasWarnings
            ? 'deleteMultipleContentsTogetherSuccessPartial'
            : 'deleteMultipleContentsTogetherSuccess';

          this.busyProcessingSelectedContents = false;
          this.$bus.$emit(alertType, {
            message: message,
            translate: true,
            timeout: 5000,
          });

          this.closeOverlayAndClearSelection();
          this.refetchContents();
        })
        .catch((error) => {
          console.error(error);
          this.$bus.$emit('alert-error', {
            message: 'genericFailedInfo',
            translate: true,
          });
        })
        .finally(() => {
          this.busyProcessingSelectedContents = false;
        });
    },
    bundleSelectedContents(selectedContents) {
      this.busyProcessingSelectedContents = true;
      return this.$api
        .bundleSelectedContents(selectedContents)
        .then((response) => {
          const hasWarnings = response.data.warnings.length > 0;

          const alertType = hasWarnings ? 'alert-warning' : 'alert-success';
          const message = hasWarnings
            ? 'bundleMultipleContentsTogetherSuccessPartial'
            : 'bundleMultipleContentsTogetherSuccess';

          this.busyProcessingSelectedContents = false;
          this.$bus.$emit(alertType, {
            message: message,
            translate: true,
            timeout: 5000,
          });

          this.closeOverlayAndClearSelection();
          this.refetchContents();
        })
        .catch((error) => {
          let message = 'bundleMultipleContentsTogetherFailure';
          if (error.response && error.response.status == 403) {
            message = 'noPermission';
          }
          this.$bus.$emit('alert-error', {
            message: message,
            translate: true,
            timeout: 10000,
          });
        })
        .finally(() => {
          this.busyProcessingSelectedContents = false;
        });
    },
    refreshDateSelectedContents(selectedContents) {
      this.busyProcessingSelectedContents = true;
      return this.$api
        .refreshDateSelectedContents(selectedContents)
        .then((response) => {
          const hasWarnings = response.data.warnings.length > 0;

          const alertType = hasWarnings ? 'alert-warning' : 'alert-success';
          const message = hasWarnings
            ? 'refreshDateMultipleContentsTogetherSuccessPartial'
            : 'refreshDateMultipleContentsTogetherSuccess';

          this.busyProcessingSelectedContents = false;
          this.$bus.$emit(alertType, {
            message: message,
            translate: true,
            timeout: 5000,
          });

          this.closeOverlayAndClearSelection();
          this.refetchContents();
        })
        .catch((error) => {
          console.error(error);
          this.$bus.$emit('alert-error', {
            message: 'genericFailFeedback',
            translate: true,
          });
        })
        .finally(() => {
          this.busyProcessingSelectedContents = false;
        });
    },
  },
};



// WEBPACK FOOTER //
// ./src/mixins/contentSelectionOverlay.js